import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import { Material } from 'three';
import { generateInvoiceNumber, generateInvoicePDF } from '../utils/invoiceUtils';
import CreateDelivery from '../components/CreateDelivery'; // Import the new component


function Job() {

  const navigate = useNavigate(); // Add this line at the start of the Job component
  const { user } = useAuth();
  const { po_number } = useParams();
  const [jobsData, setJobsData] = useState([]);
  const [isKanban, setIsKanban] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [productionLogs, setProductionLogs] = useState([]);
  const [shops, setShops] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(null);
  const [newDelivery, setNewDelivery] = useState({
    items: [],
    ship_date: new Date().toISOString().split('T')[0],
    tracking_number: '',
    notes: '',
    ship_by: 'UPS', // Default to UPS
  });  
  const [newProduction, setNewProduction] = useState({ 
    job_id: '', 
    quantity_produced: '', 
    production_date: new Date().toISOString().split('T')[0], 
    shop_id: '', 
    machine_id: '' 
  });
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [isPOModalOpen, setIsPOModalOpen] = useState(false);
  const [poModalMode, setPoModalMode] = useState('create');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState(new Date().toISOString().split('T')[0]);
  const [lineItems, setLineItems] = useState([
    { part_number: '', description: '', quantity_ordered: '', unit_price: '', expected_delivery_date: '', searchTerm: '', shop_id: '' }
  ]);  
  
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [parts, setParts] = useState([]);
  const [isGeneratingPackingSlip, setIsGeneratingPackingSlip] = useState(false);
  const [jobFiles, setJobFiles] = useState([]); // State for job files
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false); // State for file upload modal
  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false); // State for file preview modal
  const [previewFileUrl, setPreviewFileUrl] = useState(null); // State for file preview URL
  const [fileToUpload, setFileToUpload] = useState(null); // State for file to upload
  const [fileType, setFileType] = useState(''); // State for file type
  const [userRole, setUserRole] = useState(''); // State for user role
  const [jobNotes, setJobNotes] = useState(jobsData[0]?.notes || ''); // State for job notes
const [isNotesEditMode, setIsNotesEditMode] = useState(false); // State for notes edit mode

  // Fetch user profile data and role on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, email, role')
          .eq('id', user.id) // Now both IDs are UUIDs
          .single();
        if (error) throw error;
        if (data) {
          setUserRole(data.role || '');
          console.log('User role:', data.role);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err.message);
        console.log('Failed to fetch user profile.');
      }
    };

    fetchUserProfile();
  }, [user]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!user) return;
    
      setLoading(true);
      try {
        // Fetch jobs data first
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, revision, description, 
            expected_delivery_date, quantity_on_order, unit_price, shop_id, 
            quantity_shipped, status, estimated_running_days, po_start_date, 
            customer_id, produced_quantity, shipped_quantity, high_priority, kanban_job_id, notes,
            shops(id, name),
            customers(
              name, nickname,
              billing_address, billing_city, billing_state, billing_zip, 
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone
            ),
            parts!inner(part_number, part_number_master, revision, inventory_quantity)
          `)
          .eq('po_number', po_number)
          .order('po_line_number');
        if (jobsError) throw new Error(`Jobs fetch error: ${jobsError.message}`);
    
        const partNumbers = jobs.map(job => job.part_number);
        const { data: partsData, error: partsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description')
          .in('part_number', partNumbers);
        if (partsError) throw new Error(`Parts fetch error: ${partsError.message}`);
    
        const partDescriptions = {};
        partsData.forEach(part => {
          partDescriptions[part.part_number] = part.description || '';
        });
    
        const updatedJobs = jobs.map(job => ({
          ...job,
          description: partDescriptions[job.part_number] || job.description || '',
        }));
    
        setJobsData(updatedJobs || []);
        setFilteredJobs(updatedJobs || []);
    
        // Check if the job is a Kanban work order after setting jobsData
        const hasKanbanJobId = updatedJobs.some(job => job.kanban_job_id !== null);
        setIsKanban(hasKanbanJobId);
        console.log('Is Kanban:', hasKanbanJobId);
    
        const jobIds = updatedJobs.map(job => job.id);
        const { data: deliveries, error: deliveriesError } = await supabase
          .from('deliveries')
          .select(`
            package_number, job_id, po_number, po_line_number, part_number, 
            delivery_number, quantity, ship_date, tracking_number, notes, packing_slip_path, ship_by
          `)
          .in('job_id', jobIds)
          .order('job_id, delivery_number');
        if (deliveriesError) throw new Error(`Deliveries fetch error: ${deliveriesError.message}`);
        setDeliveriesData(deliveries || []);
    
        const { data: production, error: productionError } = await supabase
          .from('production_logs')
          .select('log_id, job_id, part_number, quantity_produced, production_date, shop_id, machine_id, shops(id, name), machines(id, name)')
          .in('job_id', jobIds)
          .order('production_date', { ascending: false });
        if (productionError) throw new Error(`Production logs fetch error: ${productionError.message}`);
        setProductionLogs(production || []);
    
        const { data: jobFilesData, error: jobFilesError } = await supabase
          .from('job_files')
          .select('id, po_number, file_name, file_path, file_type, uploaded_at')
          .eq('po_number', po_number);
        if (jobFilesError) throw new Error(`Job files fetch error: ${jobFilesError.message}`);
        setJobFiles(jobFilesData || []);
    
        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw new Error(`Shops fetch error: ${shopsError.message}`);
        console.log('Fetched shops:', shopsData);
        setShops(shopsData || []);
        if (!shopsData || shopsData.length === 0) {
          toast.warn('No shops found. Please add shops to the database.');
        }
    
        const { data: machinesData, error: machinesError } = await supabase
          .from('machines')
          .select('id, name');
        if (machinesError) throw new Error(`Machines fetch error: ${machinesError.message}`);
        console.log('Fetched machines:', machinesData);
        setMachines(machinesData || []);
        if (!machinesData || machinesData.length === 0) {
          toast.warn('No machines found. Please add machines to the database.');
        }
    
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select(`
            id, name, nickname, email, phone,
            billing_address, billing_city, billing_state, billing_zip,
            shipping_address, shipping_city, shipping_state, shipping_zip
          `);
        if (customersError) throw new Error(`Customers fetch error: ${customersError.message}`);
        setCustomers(customersData || []);
    
        // Fetch all parts for the search functionality
        const { data: allPartsData, error: allPartsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description');
        if (allPartsError) throw new Error(`Parts fetch error: ${allPartsError.message}`);
        setParts(allPartsData || []);
      } catch (err) {
        console.error('Error fetching job details:', err.message);
        toast.error(`Failed to fetch job details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchJobDetails();
  }, [user, po_number]);


  useEffect(() => {
    if (jobsData.length > 0) {
      document.title = jobsData[0]?.po_number || 'Purchase Order';
    } else {
      document.title = 'Loading...';
    }
  }, [jobsData]);


  // Add refreshJobsData function
  const refreshJobsData = async () => {
    try {
      const { data: refreshedJobsData, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description,
          expected_delivery_date, quantity_on_order, unit_price, shop_id,
          quantity_shipped, status, estimated_running_days, po_start_date,
          customer_id, produced_quantity, shipped_quantity, high_priority, kanban_job_id, notes,
          shops(id, name),
          customers(
            name, nickname,
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone,
            net_terms
          ),
          parts!inner(part_number, inventory_quantity, part_number_master, revision)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);

      setJobsData(refreshedJobsData || []);
      setFilteredJobs(refreshedJobsData || []);
    } catch (err) {
      console.error('Error refreshing jobs data:', err);
      toast.error(`Failed to refresh jobs data: ${err.message}`);
    }
  };


  const handleSaveNotes = async () => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ notes: jobNotes })
        .eq('po_number', po_number);
      if (error) throw new Error(`Failed to save notes: ${error.message}`);
  
      setIsNotesEditMode(false);
      toast.success('Notes saved successfully!');
    } catch (err) {
      console.error('Error saving notes:', err);
      toast.error(`Failed to save notes: ${err.message}`);
    }
  };

  

const handleAddProduction = async () => {
    if (
      !newProduction.job_id ||
      !newProduction.quantity_produced ||
      !newProduction.production_date ||
      !newProduction.shop_id ||
      !newProduction.machine_id
    ) {
      alert('Please fill in all production fields.');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('production_logs')
        .insert({
          job_id: parseInt(newProduction.job_id),
          part_number: jobsData.find(j => j.id === parseInt(newProduction.job_id)).part_number,
          quantity_produced: parseInt(newProduction.quantity_produced),
          production_date: newProduction.production_date,
          shop_id: parseInt(newProduction.shop_id),
          machine_id: parseInt(newProduction.machine_id),
        })
        .select();
      if (error) throw error;
  
      const newLog = {
        ...data[0],
        shops: { name: shops.find(s => s.id === parseInt(newProduction.shop_id))?.name },
        machines: { name: machines.find(m => m.id === parseInt(newProduction.machine_id))?.name },
      };
      setProductionLogs(prevLogs => [...prevLogs, newLog]);
  
      // Refresh jobs data to reflect the updated status
      const { data: refreshedJobsData, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description,
          expected_delivery_date, quantity_on_order, unit_price, shop_id,
          quantity_shipped, status, estimated_running_days, po_start_date,
          customer_id, produced_quantity, shipped_quantity, notes,
          shops(id, name),
          customers(
            name, nickname,
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone
          ),
          parts!inner(part_number, inventory_quantity)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
      setJobsData(refreshedJobsData || []);
      setFilteredJobs(refreshedJobsData || []);
  
      setNewProduction({
        job_id: '',
        quantity_produced: '',
        production_date: new Date().toISOString().split('T')[0],
        shop_id: '',
        machine_id: '',
      });
      setIsProductionModalOpen(false);
    } catch (err) {
      console.error('Error adding production log:', err);
      toast.error(`Failed to add production log: ${err.message}`);
    }
  };

  const updateJobField = (jobId, field, value) => {
    setJobsData(jobsData.map(job =>
      job.id === jobId ? { ...job, [field]: field === 'quantity_shipped' ? parseInt(value) : value } : job
    ));
    setFilteredJobs(filteredJobs.map(job =>
      job.id === jobId ? { ...job, [field]: field === 'quantity_shipped' ? parseInt(value) : value } : job
    ));
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  
    const sortedJobs = [...filteredJobs].sort((a, b) => {
      let aValue = a[field] || '';
      let bValue = b[field] || '';
      if (field === 'produced') {
        aValue = a.produced_quantity;
        bValue = b.produced_quantity;
      } else if (field === 'shipped') {
        aValue = a.shipped_quantity;
        bValue = b.shipped_quantity;
      } else if (field === 'remaining') {
        aValue = a.quantity_on_order - a.shipped_quantity;
        bValue = b.quantity_on_order - b.shipped_quantity;
      } else if (field === 'inventory') {
        aValue = a.parts?.inventory_quantity || 0;
        bValue = b.parts?.inventory_quantity || 0;
      } else if (field === 'needed') {
        aValue = Math.max(0, a.quantity_on_order - (a.parts?.inventory_quantity || 0) - a.produced_quantity);
        bValue = Math.max(0, b.quantity_on_order - (b.parts?.inventory_quantity || 0) - b.produced_quantity);
      }
      if (field === 'expected_delivery_date' || field === 'po_start_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return newDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (typeof aValue === 'number') return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setFilteredJobs(sortedJobs);
  };

  const handleFilter = () => {
    let filtered = [...jobsData];
    if (searchTerm) {
      filtered = filtered.filter(job =>
        job.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus) {
      filtered = filtered.filter(job => job.status === filterStatus);
    }
    setFilteredJobs(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [searchTerm, filterStatus]);

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'not_started':
        return 'bg-red-100 text-red-800';
      case 'awaiting_production':
        return 'bg-orange-100 text-orange-800';
      case 'in_production':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-green-100 text-green-800';
      case 'partially_shipped':
        return 'bg-blue-100 text-blue-800';
      case 'ready_for_shipment':
        return 'bg-purple-100 text-purple-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const addDeliveryItem = () => {
    setNewDelivery(prev => ({
      ...prev,
      items: [...prev.items, { job_id: '', quantity: '' }],
    }));
  };

  const updateDeliveryItem = (index, field, value) => {
    const updatedItems = newDelivery.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setNewDelivery(prev => ({ ...prev, items: updatedItems }));
  };

  const removeDeliveryItem = (index) => {
    setNewDelivery(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleViewPackingSlip = async (filePath) => {
    try {
      const { data, error } = await supabase.storage
        .from('delivery-files')
        .createSignedUrl(filePath, 60);
      if (error) throw new Error(`Failed to generate view URL: ${error.message}`);
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing packing slip:', err);
      toast.error(`Failed to view packing slip: ${err.message}`);
    }
  };

  const handleDownloadPackingSlip = async (filePath) => {
    try {
      const { data, error } = await supabase.storage
        .from('delivery-files')
        .download(filePath);
      if (error) throw new Error(`Failed to download packing slip: ${error.message}`);

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Packing slip downloaded successfully!');
    } catch (err) {
      console.error('Error downloading packing slip:', err);
      toast.error(`Failed to download packing slip: ${err.message}`);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { part_number: '', description: '', quantity_ordered: '', unit_price: '', expected_delivery_date: '', searchTerm: '' }]);
  };

  const updateLineItem = (index, field, value) => {
    const updatedItems = lineItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setLineItems(updatedItems);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleSavePO = async () => {
    if (
      !poNumber ||
      !poDate ||
      lineItems.some(
        (item) =>
          !item.part_number ||
          !item.quantity_ordered ||
          !item.unit_price ||
          !item.expected_delivery_date
      )
    ) {
      setError('Please fill in all required fields for each line item.');
      return;
    }
  
    try {
      // Check and insert new part numbers into the parts table if they don't exist
      const partNumbers = [...new Set(lineItems.map(item => item.part_number))]; // Get unique part numbers
      const { data: existingParts, error: partsError } = await supabase
        .from('parts')
        .select('part_number')
        .in('part_number', partNumbers);
      if (partsError) throw new Error(`Failed to fetch parts: ${partsError.message}`);
  
      const existingPartNumbers = existingParts.map(part => part.part_number);
      const newPartNumbers = partNumbers.filter(partNumber => !existingPartNumbers.includes(partNumber));
  
      if (newPartNumbers.length > 0) {
        const newParts = newPartNumbers.map(partNumber => {
          const lineItem = lineItems.find(item => item.part_number === partNumber);
          return {
            part_number_master: partNumber.split('-').slice(0, -1).join('-'), // Extract part_number_master from part_number
            revision: partNumber.split('-').pop() || '0', // Extract revision or default to '0'
            description: lineItem?.description || '',
            inventory_quantity: 0, // Default inventory quantity
          };
        });
        const { error: insertPartsError } = await supabase
          .from('parts')
          .insert(newParts);
        if (insertPartsError) throw new Error(`Failed to insert new parts: ${insertPartsError.message}`);
      }
  
      if (poModalMode === 'create') {
        const jobsToInsert = lineItems.map((item, index) => ({
          po_number: poNumber,
          po_line_number: index + 1,
          part_number: item.part_number,
          description: item.description || '',
          quantity_on_order: parseInt(item.quantity_ordered, 10),
          unit_price: parseFloat(item.unit_price),
          expected_delivery_date: item.expected_delivery_date,
          shop_id: item.shop_id ? parseInt(item.shop_id) : null, // Include shop_id
          po_start_date: poDate,
          status: 'not_started',
          customer_id: jobsData[0]?.customer_id || null,
          produced_quantity: 0,
          shipped_quantity: 0,
        }));
  
        const { error } = await supabase.from('jobs').insert(jobsToInsert);
        if (error) throw new Error(`Failed to create PO: ${error.message}`);
  
        toast.success('Purchase Order created successfully!');
      } else {
        // Fetch the current jobs from the database to get the correct customer_id
        const { data: currentJobs, error: fetchJobsError } = await supabase
          .from('jobs')
          .select('id, customer_id, shop_id, shipped_quantity, produced_quantity, part_number')
          .eq('po_number', po_number); // Use the original po_number from useParams
        if (fetchJobsError) throw new Error(`Failed to fetch current jobs: ${fetchJobsError.message}`);
  
        // Ensure all jobs have the same customer_id
        const customerId = currentJobs[0]?.customer_id || null;
        if (currentJobs.some(job => job.customer_id !== customerId)) {
          console.warn('Inconsistent customer_id values detected for jobs in PO:', po_number);
        }
  
        const updatedJobs = lineItems.map((item, index) => {
          const oldJob = currentJobs.find((j) => j.id === item.job_id);
          const shipped = oldJob ? oldJob.shipped_quantity : 0;
          const produced = oldJob ? oldJob.produced_quantity : 0;
          const inventory = oldJob ? (parts.find(p => p.part_number === oldJob.part_number)?.inventory_quantity || 0) : 0;
          const quantityOnOrder = parseInt(item.quantity_ordered, 10);
          const status = determineStatus(shipped, produced, inventory, quantityOnOrder);
          return {
            id: oldJob?.id,
            po_number: poNumber, // Use the new poNumber
            po_line_number: index + 1,
            part_number: item.part_number,
            description: item.description || '',
            quantity_on_order: quantityOnOrder,
            unit_price: parseFloat(item.unit_price),
            expected_delivery_date: item.expected_delivery_date,
            shop_id: item.shop_id ? parseInt(item.shop_id) : null, // Include shop_id
            po_start_date: poDate,
            status,
            customer_id: customerId, // Use the fetched customer_id
          };
        });
  
        for (const row of updatedJobs) {
          if (row.id) {
            const { error: updateError } = await supabase
              .from('jobs')
              .update({
                po_number: row.po_number, // Update po_number
                po_line_number: row.po_line_number,
                part_number: row.part_number,
                description: row.description,
                quantity_on_order: row.quantity_on_order,
                unit_price: row.unit_price,
                expected_delivery_date: row.expected_delivery_date,
                shop_id: row.shop_id, // Update shop_id
                po_start_date: row.po_start_date,
                status: row.status,
                customer_id: row.customer_id, // Ensure customer_id is preserved
              })
              .eq('id', row.id);
            if (updateError) throw new Error(`Failed to update job ${row.id}: ${updateError.message}`);
          } else {
            const { error: insertError } = await supabase.from('jobs').insert(row);
            if (insertError) throw new Error(`Failed to insert new job: ${insertError.message}`);
          }
        }
  
        const updatedJobIds = updatedJobs.map((u) => u.id).filter((id) => !!id);
        const jobsToDelete = currentJobs.filter((j) => !updatedJobIds.includes(j.id));
        for (const job of jobsToDelete) {
          const { error: deleteLogsError } = await supabase
            .from('production_logs')
            .delete()
            .eq('job_id', job.id);
          if (deleteLogsError) throw new Error(`Failed to delete production logs for job ${job.id}: ${deleteLogsError.message}`);
  
          const { error: deleteJobError } = await supabase.from('jobs').delete().eq('id', job.id);
          if (deleteJobError) throw new Error(`Failed to delete job ${job.id}: ${deleteJobError.message}`);
        }
  
        const { data: updatedJobsData, error: fetchError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, revision, description,
            expected_delivery_date, quantity_on_order, unit_price, shop_id,
            quantity_shipped, status, estimated_running_days, po_start_date,
            customer_id, produced_quantity, shipped_quantity, notes,
            shops(id, name),
            customers(
              name, nickname,
              billing_address, billing_city, billing_state, billing_zip, 
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone
            ),
            parts!inner(part_number, part_number_master, revision, inventory_quantity)
          `)
          .eq('po_number', poNumber) // Use the new poNumber to fetch updated jobs
          .order('po_line_number');
        if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
        setJobsData(updatedJobsData || []);
        setFilteredJobs(updatedJobsData || []);
        toast.success('Purchase Order updated successfully!');
  
        // Redirect to the new PO URL
        navigate(`/jobs/${poNumber}`);
      }
  
      setPoNumber('');
      setPoDate(new Date().toISOString().split('T')[0]);
      setLineItems([
        {
          job_id: null,
          part_number: '',
          description: '',
          quantity_ordered: '',
          unit_price: '',
          expected_delivery_date: '',
          searchTerm: '',
          shop_id: '',
        },
      ]);
      setError('');
      setIsPOModalOpen(false);
    } catch (err) {
      console.error('Error saving PO:', err);
      setError(`Failed to save PO: ${err.message}`);
    }
  };

  const handleEditPO = () => {
    setPoNumber(po_number);
    setPoDate(jobsData[0]?.po_start_date || new Date().toISOString().split('T')[0]);
    setLineItems(
      jobsData.map(job => ({
        job_id: job.id,
        part_number: job.part_number,
        description: job.description || '',
        quantity_ordered: job.quantity_on_order.toString(),
        unit_price: job.unit_price.toString(),
        expected_delivery_date: job.expected_delivery_date || '',
        shop_id: job.shop_id || '',
        searchTerm: '', // Add searchTerm
      }))
    );
    setPoModalMode('edit');
    setIsPOModalOpen(true);
  };

  const calculateShippedPercentage = (shipped, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    return Math.min((shipped / quantityOnOrder) * 100, 100);
  };

  const calculateProgressPercentage = (shipped, produced, inventory, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    const totalAvailable = shipped + (inventory || 0);
    return Math.min((totalAvailable / quantityOnOrder) * 100, 100);
  };

  const determineStatus = (shipped, produced, inventory, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 'not_started';
    const shippedPercentage = calculateShippedPercentage(shipped, quantityOnOrder);
    const progressPercentage = calculateProgressPercentage(shipped, produced, inventory, quantityOnOrder);
    if (shippedPercentage === 100) return 'closed';
    if (progressPercentage === 0) return 'not_started';
    if (shipped > 0 && shippedPercentage < 100) return 'partially_shipped';
    if (progressPercentage >= 100) return 'ready_for_shipment';
    if (produced > 0 && shipped === 0) return 'in_production';
    if (inventory > 0 && produced === 0 && shipped === 0) return 'awaiting_production';
    return 'in_progress';
  };

  const handleDeleteProductionLog = async (logId) => {
    const userConfirmed = window.confirm('Are you sure you want to delete this production log?');
    if (!userConfirmed) return;
  
    try {
      const { error: deleteError } = await supabase
        .from('production_logs')
        .delete()
        .eq('log_id', logId);
      if (deleteError) throw new Error(`Failed to delete production log: ${deleteError.message}`);
  
      setProductionLogs(productionLogs.filter(log => log.log_id !== logId));
  
      // Refresh jobs data to reflect the updated status
      const { data: refreshedJobsData, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description,
          expected_delivery_date, quantity_on_order, unit_price, shop_id,
          quantity_shipped, status, estimated_running_days, po_start_date,
          customer_id, produced_quantity, shipped_quantity, notes,
          shops(id, name),
          customers(
            name, nickname,
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone
          ),
          parts!inner(part_number, inventory_quantity, part_number_master)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
      setJobsData(refreshedJobsData || []);
      setFilteredJobs(refreshedJobsData || []);
  
      toast.success('Production log deleted successfully!');
    } catch (err) {
      console.error('Error deleting production log:', err);
      toast.error(`Failed to delete production log: ${err.message}`);
    }
  };

  const handleSelectCustomer = async (customerId) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ customer_id: customerId })
        .eq('po_number', po_number);
      if (error) throw new Error(`Failed to update customer: ${error.message}`);
  
      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description, 
          expected_delivery_date, quantity_on_order, unit_price, shop_id, 
          quantity_shipped, status, estimated_running_days, po_start_date, 
          customer_id, produced_quantity, shipped_quantity, notes,
          shops(id, name), customers(name, shipping_address, shipping_city, shipping_state, shipping_zip),
          parts!inner(part_number, inventory_quantity)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
      setJobsData(data || []);
      setFilteredJobs(data || []);
      setIsCustomerModalOpen(false);
      setCustomerSearchTerm('');
      toast.success('Customer updated successfully!');
    } catch (err) {
      console.error('Error updating customer:', err);
      toast.error(`Failed to update customer: ${err.message}`);
    }
  };

  // Add functions to handle part selection and creation
const handlePartSelect = (index, part) => {
  const updatedItems = lineItems.map((item, i) =>
    i === index ? { ...item, part_number: part.part_number, description: part.description, searchTerm: '' } : item
  );
  setLineItems(updatedItems);
};

const handleCreatePart = async (index) => {
  const searchTerm = lineItems[index].searchTerm;
  if (!searchTerm) return;
  const newPart = {
    part_number_master: searchTerm,
    revision: '0', // Default revision
    description: `New Part: ${searchTerm}`,
    inventory_quantity: 0,
  };
  try {
    const { data, error } = await supabase
      .from('parts')
      .insert(newPart)
      .select('part_number, description')
      .single();
    if (error) throw error;
    setParts([...parts, data]);
    handlePartSelect(index, data);
  } catch (err) {
    console.error('Error creating part:', err);
    setError('Failed to create new part: ' + err.message);
  }
};

const updateLineItemMultiple = (index, updates) => {
  setLineItems(prev =>
    prev.map((item, i) =>
      i === index ? { ...item, ...updates } : item
    )
  );
};

const handleDeletePO = async () => {
  const userConfirmed = window.confirm('ARE YOU SURE YOU WANT TO DELETE THIS PO? YOU WILL BE DELETING ALL PRODUCTION LOGS AND DELIVERY RECORDS! THIS ACTION CANNOT BE UNDONE!');
  if (!userConfirmed) return;

  try {
    // Get all job IDs for the PO
    const jobIds = jobsData.map(job => job.id);

    // Delete associated production logs
    const { error: deleteLogsError } = await supabase
      .from('production_logs')
      .delete()
      .in('job_id', jobIds);
    if (deleteLogsError) throw new Error(`Failed to delete production logs: ${deleteLogsError.message}`);

    // Delete associated deliveries
    const { error: deleteDeliveriesError } = await supabase
      .from('deliveries')
      .delete()
      .in('job_id', jobIds);
    if (deleteDeliveriesError) throw new Error(`Failed to delete deliveries: ${deleteDeliveriesError.message}`);

    // Delete the jobs associated with the PO
    const { error: deleteJobsError } = await supabase
      .from('jobs')
      .delete()
      .eq('po_number', po_number);
    if (deleteJobsError) throw new Error(`Failed to delete jobs: ${deleteJobsError.message}`);

    // Redirect to the Jobs page
    toast.success('Purchase Order deleted successfully!');
    navigate('/jobs');
  } catch (err) {
    console.error('Error deleting PO:', err);
    toast.error(`Failed to delete PO: ${err.message}`);
  }
};

const handleFileUpload = async () => {
  if (!fileToUpload || !fileType) {
    toast.error('Please select a file and file type.');
    return;
  }

  try {
    const filePath = `job-files/${po_number}/${fileToUpload.name}`;
    const { error: uploadError } = await supabase.storage
      .from('job-files')
      .upload(filePath, fileToUpload);
    if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);

    const { data, error: insertError } = await supabase
      .from('job_files')
      .insert({
        po_number,
        file_name: fileToUpload.name,
        file_path: filePath,
        file_type: fileType,
        uploaded_by: user.id,
      })
      .select();
    if (insertError) throw new Error(`Failed to insert file record: ${insertError.message}`);

    setJobFiles([...jobFiles, data[0]]);
    setFileToUpload(null);
    setFileType('');
    setIsFileUploadModalOpen(false);
    toast.success('File uploaded successfully!');
  } catch (err) {
    console.error('Error uploading file:', err);
    toast.error(`Failed to upload file: ${err.message}`);
  }
};

const handleFileDownload = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from('job-files')
      .download(filePath);
    if (error) throw new Error(`Failed to download file: ${error.message}`);

    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filePath.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('File downloaded successfully!');
  } catch (err) {
    console.error('Error downloading file:', err);
    toast.error(`Failed to download file: ${err.message}`);
  }
};

const handleFilePreview = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from('job-files')
      .createSignedUrl(filePath, 60); // URL valid for 60 seconds
    if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);

    // Open the file in a new tab
    window.open(data.signedUrl, '_blank');

    // Refresh the current tab to reset state (if needed)
    window.location.reload();
  } catch (err) {
    console.error('Error generating preview URL:', err);
    toast.error(`Failed to preview file: ${err.message}`);
  }
};


const handleFileDelete = async (fileId, filePath) => {
  const userConfirmed = window.confirm('Are you sure you want to delete this file?');
  if (!userConfirmed) return;

  try {
    const { error: deleteError } = await supabase.storage
      .from('job-files')
      .remove([filePath]);
    if (deleteError) throw new Error(`Failed to delete file from storage: ${deleteError.message}`);

    const { error: dbError } = await supabase
      .from('job_files')
      .delete()
      .eq('id', fileId);
    if (dbError) throw new Error(`Failed to delete file record: ${dbError.message}`);

    setJobFiles(jobFiles.filter(file => file.id !== fileId));
    toast.success('File deleted successfully!');
  } catch (err) {
    console.error('Error deleting file:', err);
    toast.error(`Failed to delete file: ${err.message}`);
  }
};

// Add these state variables near your other useState declarations
const [isHighPriorityModalOpen, setIsHighPriorityModalOpen] = useState(false);

// Function to toggle the high_priority flag for a given job
const flagHighPriority = async (job) => {
  const newPriority = job.high_priority ? 0 : 1;
  try {
    const { error } = await supabase
      .from('jobs')
      .update({ high_priority: newPriority })
      .eq('id', job.id);
    if (error) throw error;
    // Update jobsData state with the new high_priority flag
    setJobsData((prevJobs) =>
      prevJobs.map((j) => (j.id === job.id ? { ...j, high_priority: newPriority } : j))
    );
    toast.success(
      `Job ${job.po_line_number} ${newPriority ? 'flagged as High Priority' : 'removed from High Priority'}.`
    );
  } catch (err) {
    console.error('Error updating high priority:', err);
    toast.error('Failed to update high priority.');
  }
};

useEffect(() => {
  if (jobsData.length > 0) {
    setJobNotes(jobsData[0]?.notes || '');
  }
}, [jobsData]);


  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;
  if (jobsData.length === 0) return <div className="text-center text-red-500 py-10">No jobs found for PO {po_number}</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto bg-gray-50">
        <div className="flex justify-between items-center mb-6 bg-gray-50">
          <h1 className="text-3xl font-bold text-gray-900">PO# {po_number}</h1>
          
          <div className="flex space-x-4">
          {isKanban && (
      <button
        onClick={() => {
          const kanbanJob = jobsData.find(job => job.kanban_job_id !== null);
          if (kanbanJob) {
            window.open(`/kanban/${kanbanJob.kanban_job_id}`, '_blank');
          }
        }}
        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Go to Kanban PO
      </button>
    )}
          <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                Actions
                <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => {
                          setNewProduction({ ...newProduction, job_id: jobsData[0]?.id || '' });
                          setIsProductionModalOpen(true);
                        }}
                        className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm`}
                      >
                        Add Production
                      </button>
                    )}
                  </Menu.Item>
                {!isKanban && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => {
                          setNewDelivery({ items: [{ job_id: jobsData[0]?.id || '', quantity: '' }], ship_date: new Date().toISOString().split('T')[0], tracking_number: '', notes: '' });
                          setIsDeliveryModalOpen(true);
                        }}
                        className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm`}
                      >
                        New Delivery
                      </button>
                    )}
                  </Menu.Item> )}
                {!isKanban && (
                  <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setIsFileUploadModalOpen(true)}
                          className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm`}
                        >
                          Add File
                        </button>
                      )}
                    </Menu.Item>  )}
              {userRole !== 'worker' && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleEditPO}
                        className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm`}
                      >
                        Edit PO
                      </button>
                    )}
                  </Menu.Item> )}
              {(userRole !== 'worker' && !isKanban) && (
                  <Menu.Item>
                    {({ active }) => (
                        <button
                        onClick={() => setIsCustomerModalOpen(true)}
                        className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm`}
                        >
                        Select Customer
                        </button>
                    )}
                    </Menu.Item> )}
                    {userRole !== 'worker' && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setIsHighPriorityModalOpen(true)}
                          className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm`}
                        >
                          High Priority
                        </button>
                      )}
                    </Menu.Item>
                    )}
                    {userRole !== 'worker' && (

                    <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleDeletePO}
                    className={`${active ? 'bg-red-100 text-red-900' : 'text-red-700'} block w-full text-left px-4 py-2 text-sm`}
                  >
                    Delete PO
                  </button>
                )}
              </Menu.Item>)}
                  
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
          </div>
        </div>

        {isKanban && (
          <div className="flex items-center space-x-2 mb-4 p-2 bg-orange-50 border border-orange-200 rounded-lg max-w-xl">
            <ExclamationTriangleIcon className="w-6 h-6 text-orange-500" />
            <p className="text-orange-500">This is a Kanban Work Order. Log Production for Inventory Maintenance.</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg mb-6 border border-2">
          
            <div>
              <label className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Part Number or Description"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
              />
            </div>
            
         
        </div>

        {/* Line Items Section */}
        <div className="bg-white rounded-lg overflow-hidden mb-6 border border-2">
          <h2 className="text-xl font-semibold p-4 bg-gray-50 text-gray-900">Line Items</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('po_line_number')}>
                    Line {sortField === 'po_line_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('part_number')}>
                    Part Number {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('expected_delivery_date')}>
                    Due {sortField === 'expected_delivery_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')}>
                    Status {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>

                  {!isKanban && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipped</th>
                  )}

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('quantity_on_order')}>
                    QTY {sortField === 'quantity_on_order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('inventory')}>
                    Inventory {sortField === 'inventory' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('produced')}>
                    Produced {sortField === 'produced' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  
                  {!isKanban && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('shipped')}>
                    Shipped {sortField === 'shipped' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                  </th>
                  )}

                  
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map((job) => {
                  const produced = job.produced_quantity;
                  const shipped = job.shipped_quantity;
                  const inventory = job.parts?.inventory_quantity || 0;
                  const shippedPercentage = calculateShippedPercentage(shipped, job.quantity_on_order);
                  const progressPercentage = calculateProgressPercentage(shipped, produced, inventory, job.quantity_on_order);
                  console.log(`Job ${job.id}: shipped=${shipped}, produced=${produced}, inventory=${inventory}, quantityOnOrder=${job.quantity_on_order}, progressPercentage=${progressPercentage}`);
                  return (
                    <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shops?.name || 'Unknown'}</td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.po_line_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <a
                          href={`/part/${job.part_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 transition duration-150"
                        >
                          {job.part_number}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={job.description}>{job.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {job.expected_delivery_date ? new Date(job.expected_delivery_date).toLocaleDateString() : 'KANBAN'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusChipClass(job.status)}`}>
                          {job.status.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="w-32 bg-gray-200 rounded-full h-2.5" title={`Progress: ${progressPercentage.toFixed(2)}%, Shipped: ${shipped}, Produced: ${produced}, Inventory: ${inventory}`}>
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">{progressPercentage.toFixed(0)}%</span>
                      </td>

                      {!isKanban && (
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="w-32 bg-gray-200 rounded-full h-2.5" title={`Shipped: ${shippedPercentage.toFixed(2)}%, Shipped: ${shipped}`}>
                          <div
                            className="bg-green-600 h-2.5 rounded-full"
                            style={{ width: `${shippedPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">{shippedPercentage.toFixed(0)}%</span>
                      </td> )}

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.quantity_on_order}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inventory}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{produced}</td>

                      {!isKanban && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {editMode === job.id ? (
                          <input
                            type="number"
                            value={job.quantity_shipped}
                            onChange={(e) => updateJobField(job.id, 'quantity_shipped', e.target.value)}
                            className="w-20 p-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                            min="0"
                            max={job.quantity_on_order}
                            autoFocus
                          />
                        ) : (
                          shipped
                        )}
                      </td> )}

                      
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Production Modal */}
        <Transition appear show={isProductionModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => setIsProductionModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Add New Production Log
                    </Dialog.Title>
                    <div className="mt-4">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Job</label>
                          <select
                            value={newProduction.job_id}
                            onChange={(e) => setNewProduction({ ...newProduction, job_id: e.target.value })}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          >
                            <option value="">Select Job</option>
                            {jobsData.map(job => (
                              <option key={job.id} value={job.id}>{job.po_line_number} - {job.part_number}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Quantity Produced</label>
                          <input
                            type="number"
                            value={newProduction.quantity_produced}
                            onChange={(e) => setNewProduction({ ...newProduction, quantity_produced: e.target.value })}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                            min="0"
                            placeholder="Enter quantity"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Production Date</label>
                          <input
                            type="date"
                            value={newProduction.production_date}
                            onChange={(e) => setNewProduction({ ...newProduction, production_date: e.target.value })}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Shop</label>
                          <select
                            value={newProduction.shop_id}
                            onChange={(e) => setNewProduction({ ...newProduction, shop_id: e.target.value })}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          >
                            <option value="">Select Shop</option>
                            {shops.map(shop => (
                              <option key={shop.id} value={shop.id}>{shop.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Machine</label>
                          <select
                            value={newProduction.machine_id}
                            onChange={(e) => setNewProduction({ ...newProduction, machine_id: e.target.value })}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          >
                            <option value="">Select Machine</option>
                            {machines.map(machine => (
                              <option key={machine.id} value={machine.id}>{machine.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-4">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                        onClick={() => {
                          setNewProduction({ job_id: '', quantity_produced: '', production_date: new Date().toISOString().split('T')[0], shop_id: '', machine_id: '' });
                          setIsProductionModalOpen(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                        onClick={handleAddProduction}
                      >
                        Save Production Log
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>


        {/* Production Logs Section */}
        <div className="bg-white rounded-lg p-6 mb-6 border border-2">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Production Logs</h2>
          {productionLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity Produced</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Production Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machine</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {productionLogs.map((log) => {
                    const job = jobsData.find(j => j.id === log.job_id);
                    const uniqueKey = `${log.log_id}-${log.job_id}-${log.production_date}`;
                    return (
                      <tr key={uniqueKey} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job?.po_line_number || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.part_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.quantity_produced}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.production_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.shops?.name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.machines?.name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => handleDeleteProductionLog(log.log_id)}
                            className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No production logs recorded for this PO.</p>
          )}
        </div>

        {/* Deliveries Section */}
        <div className={`bg-white rounded-lg p-6 mb-6 border border-2 ${isKanban ? 'opacity-50 pointer-events-none' : ''}`}>
  <h2 className="text-xl font-semibold mb-4 text-gray-900">Deliveries</h2>
  {isKanban ? (
    <div className="flex items-center space-x-2">
      <ExclamationTriangleIcon className="w-6 h-6 text-orange-500" />
      <p className="text-gray-600">This is a Kanban Work Order, please make deliveries through Kanban PO.</p>
    </div>
  ) : deliveriesData.length > 0 ? (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package #</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO Line #</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ship Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ship By</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking #</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Packing Slip</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {deliveriesData.map((delivery) => (
            <tr key={`${delivery.package_number}-${delivery.delivery_number}`} className="hover:bg-gray-50 transition duration-150">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.package_number}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.po_line_number}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.part_number}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.quantity}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(delivery.ship_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.ship_by || '-'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.tracking_number || '-'}</td>
              <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={delivery.notes}>{delivery.notes || '-'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {delivery.packing_slip_path ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewPackingSlip(delivery.packing_slip_path)}
                      className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 text-sm"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDownloadPackingSlip(delivery.packing_slip_path)}
                      className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 text-sm"
                    >
                      Download
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="text-gray-500">No deliveries recorded for this PO.</p>
  )}
</div>

{/* Files Section */}
<div className="bg-white rounded-lg p-4 mb-6 border-2">
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold text-gray-900">Files</h2>
    <button
      onClick={() => setIsFileUploadModalOpen(true)}
      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
    >
      Upload File
    </button>
  </div>
  {jobFiles.length > 0 ? (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded At</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {jobFiles.map((file) => (
            <tr key={file.id} className="hover:bg-gray-50 transition duration-150">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{file.file_name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{file.file_type}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(file.uploaded_at).toLocaleDateString()}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  onClick={() => handleFilePreview(file.file_path)}
                  className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 text-sm mr-2"
                >
                  Preview
                </button>
                <button
                  onClick={() => handleFileDownload(file.file_path)}
                  className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 text-sm mr-2"
                >
                  Download
                </button>
                <button
                  onClick={() => handleFileDelete(file.id, file.file_path)}
                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 text-sm"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="text-gray-500">No files uploaded for this job.</p>
  )}
</div>

        {/* Customer Section */}
            <div className="bg-white rounded-lg p-4 mb-6 border-2">
            <h2 className="text-xl font-semibold mb-4 text-gray-900">Customer</h2>
            {jobsData.length > 0 && jobsData[0].customers ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                {jobsData[0].customers.nickname && (
                    <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Nickname:</span> {jobsData[0].customers.nickname}
                    </p>
                    )}
                    <p className="text-sm text-gray-600">
                    <span className="font-medium">Name:</span> {jobsData[0].customers.name}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                    <span className="font-medium">Shipping Address:</span> {jobsData[0].customers.shipping_address || 'N/A'}, {jobsData[0].customers.shipping_city || 'N/A'}, {jobsData[0].customers.shipping_state || 'N/A'} {jobsData[0].customers.shipping_zip || 'N/A'}
                    </p>
                </div>
                <div>
                    <p className="text-sm text-gray-600">
                    <span className="font-medium">Shipping Email:</span> {jobsData[0].customers.shipping_contact_email || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                    <span className="font-medium">Shipping Phone:</span> {jobsData[0].customers.shipping_contact_phone || 'N/A'}
                    </p>
                </div>
                </div>
            ) : (
                <p className="text-gray-500">No customer assigned to this PO.</p>
            )}
            </div>

            {/* Notes Section */}
<div className="bg-white rounded-lg p-4 mb-6 border-2">
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
    <button
      onClick={() => {
        if (isNotesEditMode) {
          handleSaveNotes();
        } else {
          setIsNotesEditMode(true);
        }
      }}
      className={`px-4 py-2 rounded-lg text-white transition duration-200 shadow-sm ${
        isNotesEditMode
          ? 'bg-green-600 hover:bg-green-700'
          : 'bg-indigo-600 hover:bg-indigo-700'
      }`}
    >
      {isNotesEditMode ? 'Save Notes' : 'Edit Notes'}
    </button>
  </div>
  {isNotesEditMode ? (
    <textarea
      value={jobNotes}
      onChange={(e) => setJobNotes(e.target.value)}
      className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
      rows="5"
      placeholder="Enter notes here..."
    />
  ) : (
    <p className="text-gray-600 whitespace-pre-wrap">
      {jobNotes || 'No notes available.'}
    </p>
  )}
</div>

            {/* File Upload Modal */}
<Transition appear show={isFileUploadModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-10" onClose={() => setIsFileUploadModalOpen(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black bg-opacity-25" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
              Upload New File
            </Dialog.Title>
            <div className="mt-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">File</label>
                  <input
                    type="file"
                    onChange={(e) => setFileToUpload(e.target.files[0])}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">File Type</label>
                  <select
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  >
                    <option value="">Select File Type</option>
                    <option value="Drawing">Drawing</option>
                    <option value="PO">PO</option>
                    <option value="Material Invoice">Material Invoice</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-4">
              <button
                type="button"
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                onClick={() => {
                  setFileToUpload(null);
                  setFileType('');
                  setIsFileUploadModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                onClick={handleFileUpload}
              >
                Upload
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>

{/* File Preview Modal */}
<Transition appear show={isFilePreviewModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={() => setIsFilePreviewModalOpen(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black bg-opacity-25" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-[90vw] h-[90vh] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
              File Preview
            </Dialog.Title>
            <div className="mt-4 h-[calc(100%-6rem)] overflow-auto">
              {previewFileUrl ? (
                <iframe
                  src={previewFileUrl}
                  className="w-full h-full border border-gray-200 rounded-lg"
                  title="File Preview"
                />
              ) : (
                <p className="text-gray-500">Unable to load file.</p>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setIsFilePreviewModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>


        {/* Create/Edit PO Modal */}
<Transition appear show={isPOModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={() => setIsPOModalOpen(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold mb-4 text-gray-900">
              {poModalMode === 'create' ? 'Create Purchase Order' : 'Edit Purchase Order'}
            </Dialog.Title>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">PO Number</label>
                <input
                  type="text"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  required
                  // disabled={poModalMode === 'edit'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">PO Start</label>
                <input
                  type="date"
                  value={poDate}
                  onChange={(e) => setPoDate(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Line Items</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 mb-20">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Ordered</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lineItems.map((item, index) => {
                      const filteredParts = parts.filter(part =>
                        part.part_number.toLowerCase().includes((item.searchTerm || '').toLowerCase())
                      );
                      return (
                        <tr key={index} className="hover:bg-gray-50 transition duration-150 overflow-visible">
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 relative">
  <input
    type="text"
    value={item.searchTerm || item.part_number}
    onChange={(e) => {
      const value = e.target.value;
      updateLineItemMultiple(index, {
        searchTerm: value,
        // If the user types something different from the selected part, clear it out
        part_number: value !== item.part_number ? '' : item.part_number,
        description: value !== item.part_number ? '' : item.description,
      });
    }}
    className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
    placeholder="Search or enter part number"
  />
  {item.searchTerm && (
    <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
      {parts.filter(part =>
        part.part_number.toLowerCase().includes(item.searchTerm.toLowerCase())
      ).length > 0 ? (
        parts
          .filter(part =>
            part.part_number.toLowerCase().includes(item.searchTerm.toLowerCase())
          )
          .map((part) => (
            <div
              key={part.part_number}
              onClick={() => handlePartSelect(index, part)}
              className="px-4 py-2 text-sm text-gray-900 hover:bg-indigo-100 cursor-pointer transition duration-150"
            >
              {part.part_number} - {part.description}
            </div>
          ))
      ) : (
        <div
          onClick={() => handleCreatePart(index)}
          className="px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-100 cursor-pointer transition duration-150"
        >
          Create new part: "{item.searchTerm}"
        </div>
      )}
    </div>
  )}
</td>

                        <td className="px-4 py-2 text-sm text-gray-900">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                            placeholder="Description"
                            readOnly={!!item.part_number} // Make read-only if part_number exists
                            disabled={!!item.part_number} // Disable the field to prevent interaction
                          />
                        </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="number"
                              value={item.quantity_ordered}
                              onChange={(e) => updateLineItem(index, 'quantity_ordered', e.target.value)}
                              className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                              min="0"
                              placeholder="Qty"
                            />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                              className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                              min="0"
                              step="0.01"
                              placeholder="Price"
                            />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="date"
                              value={item.expected_delivery_date}
                              onChange={(e) => updateLineItem(index, 'expected_delivery_date', e.target.value)}
                              className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                              required
                            />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            <select
                              value={item.shop_id}
                              onChange={(e) => updateLineItem(index, 'shop_id', e.target.value)}
                              className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                            >
                              <option value="">Select Shop</option>
                              {shops.map((shop) => (
                                <option key={shop.id} value={shop.id}>
                                  {shop.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            <button
                              onClick={() => removeLineItem(index)}
                              className="text-red-600 hover:text-red-800 transition duration-150"
                              disabled={lineItems.length === 1}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button
                onClick={addLineItem}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
              >
                Add Line Item
              </button>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsPOModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePO}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
              >
                {poModalMode === 'create' ? 'Save PO' : 'Update PO'}
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>

        {/* Select Customer Modal */}
<Transition appear show={isCustomerModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={() => setIsCustomerModalOpen(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 border-2 transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold mb-4 text-gray-900">
              Select Customer
            </Dialog.Title>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Search Customers</label>
              <input
                type="text"
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
  {customers
    .filter(customer =>
      customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      (customer.email && customer.email.toLowerCase().includes(customerSearchTerm.toLowerCase())) ||
      (customer.phone && customer.phone.toLowerCase().includes(customerSearchTerm.toLowerCase()))
    )
    .map(customer => (
      <div
        key={customer.id}
        onClick={() => handleSelectCustomer(customer.id)}
        className="p-4 border border-gray-300 rounded-lg hover:bg-indigo-50 cursor-pointer transition duration-150"
      >
        <h3 className="text-lg font-medium text-gray-900">{customer.name}</h3>
        {customer.nickname && (
          <p className="text-sm text-gray-600 mt-1">
            <span className="font-medium">Nickname:</span> {customer.nickname}
          </p>
        )}
        <p className="text-sm text-gray-600 mt-1">
          <span className="font-medium">Billing Address:</span> {customer.billing_address || 'N/A'}, {customer.billing_city || 'N/A'}, {customer.billing_state || 'N/A'} {customer.billing_zip || 'N/A'}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          <span className="font-medium">Shipping Address:</span> {customer.shipping_address || 'N/A'}, {customer.shipping_city || 'N/A'}, {customer.shipping_state || 'N/A'} {customer.shipping_zip || 'N/A'}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          <span className="font-medium">Email:</span> {customer.email || 'N/A'}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          <span className="font-medium">Phone:</span> {customer.phone || 'N/A'}
        </p>
      </div>
    ))}
</div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setIsCustomerModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>


{isHighPriorityModalOpen && (
  <Transition appear show={isHighPriorityModalOpen} as={Fragment}>
    <Dialog as="div" className="relative z-50" onClose={() => setIsHighPriorityModalOpen(false)}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black bg-opacity-25" />
      </Transition.Child>
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <Dialog.Title className="text-2xl font-bold text-gray-900">
                High Priority
              </Dialog.Title>
              <div className="mt-4 space-y-4">
                {jobsData.map((job) => (
                  <div key={job.id} className="flex justify-between items-center p-4 border rounded-lg shadow-sm">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">
                        Line {job.po_line_number}
                      </p>
                      <p className="text-sm text-gray-600">
                        Part: {job.part_number}
                      </p>
                      <p className="text-sm text-gray-600">
                        {job.description}
                      </p>
                    </div>
                    <button
                      onClick={() => flagHighPriority(job)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        job.high_priority ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                      }`}
                    >
                      {job.high_priority ? 'Remove from High Priority' : 'Flag as High Priority'}
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setIsHighPriorityModalOpen(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>
)}

<CreateDelivery
  isOpen={isDeliveryModalOpen}
  onClose={() => setIsDeliveryModalOpen(false)}
  jobsData={jobsData}
  poNumber={po_number}
  deliveriesData={deliveriesData}
  setDeliveriesData={setDeliveriesData}
  isKanban={false}
  usePartNumberMaster={true}
  onDeliveryCreated={refreshJobsData}
/>

      </div>
    </div>
  );
}

export default Job;